/** Append with a single space between non-empty parts. */
export function joinText(a: string, b: string): string {
  if (!a) return b
  if (!b) return a
  return /\s$/.test(a) ? a + b : `${a} ${b}`
}

export interface DictationSubmit {
  /** End the session and send what it transcribed — once, whoever asks. */
  run: () => Promise<void>
  /**
   * A hand-off is in flight. The dictation UI is already idle at this point
   * (the session is flushing its last utterance), so a submit must join the
   * hand-off instead of sending the composer value on its own.
   */
  pending: () => boolean
}

/**
 * The single way a dictation session turns into a sent message.
 *
 * Pressing submit while dictation runs and the silence auto-stop both end up
 * here, and they can arrive in either order within the same second (the stop
 * flush waits for the service to finalize the last utterance). The in-flight
 * promise is the token: the first caller stops the session and sends the
 * flushed text, every later caller awaits that same promise. Two sends of one
 * dictation — the duplicate turn PR #17 chased server-side — cannot happen.
 */
export function createDictationSubmit(deps: {
  /** Ends the session and resolves with its full text; idempotent per session. */
  stop: () => Promise<string>
  /** What the user had typed before pressing the mic. */
  base: () => string
  /** Drop the dictation base — the text below belongs to the message now. */
  reset: () => void
  /** Send it; false when sending is not possible right now. */
  send: (text: string) => boolean
  /** Sending was refused — keep the text in the composer instead. */
  keep: (text: string) => void
}): DictationSubmit {
  let inFlight: Promise<void> | null = null
  const run = (): Promise<void> => {
    if (inFlight) return inFlight
    inFlight = (async () => {
      // Awaiting stop() is the point of the whole hand-off: it closes the
      // session (billed per start) and returns the utterance that was still
      // in the air when submit was pressed, so the message is complete.
      const dictated = await deps.stop()
      const text = joinText(deps.base(), dictated)
      deps.reset()
      if (!deps.send(text)) deps.keep(text)
    })().finally(() => {
      inFlight = null
    })
    return inFlight
  }
  return { run, pending: () => inFlight !== null }
}
